import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Check, Download, FileText } from 'lucide';
import { api } from '../api.js';
import { lucideIcon } from '../icon.js';

/**
 * lab-certificaat — het analysecertificaat van het lab downloaden, met feedback tijdens het
 * wachten.
 *
 * Het certificaat komt rechtstreeks van het lab en wordt niet bij ons bewaard. Bij SGS duurt
 * dat op productie vijf tot tien seconden. Een knop die al die tijd niets zegt, wordt
 * opnieuw ingedrukt of voor kapot gehouden; daarom loopt er een balk met een schatting van de
 * resterende tijd, en zegt hij daarna dat het bestand in je downloadmap staat.
 *
 * Twee maten, dezelfde staten:
 *   - `compact`  in een rij van het overzicht: een kleine knop
 *   - `kaart`    op de pagina van de opdracht: een eigen kaart met uitleg
 *
 * Tijdens het ophalen staat de knop op slot: één klik is één vraag aan het lab.
 */

/** Hoe lang het lab er meestal over doet. Alleen voor de balk; het echte antwoord bepaalt het einde. */
const VERWACHT_MS = 6000;
/** Zo lang blijft 'Gedownload' staan in de compacte knop; de kaart houdt het vast. */
const KLAAR_MS = 8000;

export class LabCertificaat extends LitElement {
  static properties = {
    /** Het ordernummer bij het lab; daarmee vraagt de backend het certificaat op. */
    labOrderNumber: { type: String },
    batchId: { type: String },
    labName: { type: String },
    variant: { type: String },   // 'compact' | 'kaart'
    _fase: { state: true },      // 'klaar' | 'bezig' | 'gedownload' | 'fout'
    _verstreken: { state: true },
    _bestand: { state: true },
    _fout: { state: true },
  };

  constructor() {
    super();
    this.variant = 'compact';
    this.labName = 'het lab';
    this._fase = 'klaar';
    this._verstreken = 0;
    this._bestand = '';
    this._fout = '';
  }

  disconnectedCallback() {
    clearInterval(this._tik);
    clearTimeout(this._terug);
    super.disconnectedCallback();
  }

  async _haal(e) {
    // In een rij die zelf een link is: de klik is voor de knop, niet voor de rij.
    e?.preventDefault();
    e?.stopPropagation();
    if (this._fase === 'bezig' || !this.labOrderNumber) return;
    clearTimeout(this._terug);
    this._fase = 'bezig';
    this._fout = '';
    const start = Date.now();
    this._verstreken = 0;
    this._tik = setInterval(() => { this._verstreken = Date.now() - start; }, 100);
    try {
      const { blob, filename } = await api.downloadLabOrderReport(this.labOrderNumber);
      const naam = filename || `analysecertificaat-${this.batchId || this.labOrderNumber}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = naam;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      this._bestand = naam;
      this._fase = 'gedownload';
      if (this.variant !== 'kaart') {
        this._terug = setTimeout(() => { this._fase = 'klaar'; }, KLAAR_MS);
      }
    } catch (err) {
      this._fout = err?.message || '';
      this._fase = 'fout';
    } finally {
      clearInterval(this._tik);
    }
  }

  /** Resterende seconden volgens de schatting; daarna 'even geduld' in plaats van '0s'. */
  get _nog() {
    const s = Math.ceil((VERWACHT_MS - this._verstreken) / 1000);
    return s > 0 ? s : 0;
  }

  /** De balk loopt naar 95%: de laatste stap is het echte antwoord, niet de klok. */
  get _breedte() {
    return `${Math.min(95, (this._verstreken / VERWACHT_MS) * 95)}%`;
  }

  render() {
    return this.variant === 'kaart' ? this._renderKaart() : this._renderCompact();
  }

  _renderCompact() {
    if (this._fase === 'bezig') {
      return html`
        <span class="c bezig" title="Het certificaat wordt opgehaald bij ${this.labName}">
          <span>Ophalen… ${this._nog ? `${this._nog}s` : ''}</span>
          <span class="balk"><span style="width:${this._breedte}"></span></span>
        </span>`;
    }
    if (this._fase === 'gedownload') {
      return html`<span class="c klaar" title=${this._bestand}>${unsafeSVG(lucideIcon(Check, 14))} Gedownload</span>`;
    }
    if (this._fase === 'fout') {
      return html`<button class="c fout" title=${this._fout || 'Ophalen mislukt'} @click=${this._haal}>Mislukt · opnieuw</button>`;
    }
    return html`
      <button class="c" title="Analysecertificaat van ${this.labName} downloaden" @click=${this._haal}>
        ${unsafeSVG(lucideIcon(Download, 13))} Certificaat
      </button>`;
  }

  _renderKaart() {
    return html`
      <div class="kaart ${this._fase}">
        <div class="kop">
          <span class="icoon">${unsafeSVG(lucideIcon(FileText, 18))}</span>
          <div>
            <strong>Analysecertificaat</strong>
            <span>Officieel rapport van ${this.labName}${this.batchId ? ` · batch ${this.batchId}` : ''}</span>
          </div>
        </div>
        ${this._fase === 'klaar' ? html`
          <p>Met methoden, accreditatie en ondertekening. We halen het direct bij ${this.labName} op;
             dat duurt een paar seconden.</p>
          <button class="hoofd" @click=${this._haal}>${unsafeSVG(lucideIcon(Download, 15))} Download PDF</button>` : ''}
        ${this._fase === 'bezig' ? html`
          <p>${this.labName} stelt het certificaat samen. Je download start vanzelf zodra het binnen is.</p>
          <div class="melding groen">
            <div class="regel">
              <span>Ophalen bij ${this.labName}…</span>
              <span class="tijd">${this._nog ? `nog ~${this._nog}s` : 'nog even geduld'}</span>
            </div>
            <span class="balk"><span style="width:${this._breedte}"></span></span>
          </div>` : ''}
        ${this._fase === 'gedownload' ? html`
          <div class="melding groen rij">
            ${unsafeSVG(lucideIcon(Check, 16))}
            <div class="tekst">
              <strong>Staat in je downloadmap</strong>
              <span>${this._bestand}</span>
            </div>
            <button class="tekstknop" @click=${this._haal}>Opnieuw</button>
          </div>` : ''}
        ${this._fase === 'fout' ? html`
          <div class="melding rood rij">
            <div class="tekst">
              <strong>Ophalen mislukt</strong>
              <span title=${this._fout}>${this._fout || `${this.labName} reageerde niet op tijd.`} Probeer het zo nog eens.</span>
            </div>
            <button class="knop-rood" @click=${this._haal}>Opnieuw proberen</button>
          </div>` : ''}
      </div>`;
  }

  static styles = css`
    :host { display:inline-block; }
    :host([variant='kaart']) { display:block; }
    button { font-family:inherit; cursor:pointer; }
    svg { flex:none; }

    /* Compact: even hoog als de link ernaast, vaste breedte zodat de rij niet verspringt. */
    .c { box-sizing:border-box; display:inline-flex; align-items:center; justify-content:center; gap:5px;
         min-width:118px; height:30px; padding:0 9px; border:1px solid var(--mv-border); border-radius:8px;
         background:var(--mv-surface); color:var(--mv-text); font-size:0.78rem; font-weight:600; white-space:nowrap; }
    button.c:hover { border-color:var(--mv-primary); color:var(--mv-primary); }
    .c.bezig { flex-direction:column; align-items:stretch; gap:4px; background:var(--mv-surface-alt);
               color:var(--mv-text-sub); font-size:0.74rem; }
    .c.bezig .balk { height:3px; }
    .c.klaar { border-color:transparent; background:none; color:var(--mv-primary); }
    .c.fout { border-color:var(--mv-danger-border, #F8C9D6); background:var(--mv-danger-bg); color:var(--mv-danger); }

    .balk { display:block; height:4px; border-radius:2px; background:var(--mv-border); overflow:hidden; }
    .balk > span { display:block; height:100%; background:var(--mv-primary); transition:width .1s linear; }

    /* Kaart */
    .kaart { box-sizing:border-box; height:100%; display:flex; flex-direction:column; gap:12px;
             padding:20px 22px; border:1px solid var(--mv-border); border-radius:14px; background:var(--mv-surface); }
    .kaart.bezig, .kaart.gedownload { border-color:var(--mv-primary-tint-border, #CFE6C8); }
    .kaart.fout { border-color:var(--mv-danger-border, #F8C9D6); }
    .kop { display:flex; align-items:center; gap:10px; }
    .kop strong { display:block; font-size:0.95rem; color:var(--mv-text); }
    .kop span { font-size:0.8rem; color:var(--mv-text-sub); }
    .icoon { width:36px; height:36px; flex:none; display:flex; align-items:center; justify-content:center;
             border-radius:9px; background:var(--mv-primary-light); color:var(--mv-primary-hover); }
    .kaart p { margin:0; font-size:0.8rem; line-height:1.5; color:var(--mv-text-sub); }
    .hoofd { display:flex; align-items:center; justify-content:center; gap:7px; height:40px; border:0;
             border-radius:9px; background:var(--mv-primary); color:#fff; font-size:0.88rem; font-weight:600; }
    .hoofd:hover { background:var(--mv-primary-hover); }
    .melding { display:flex; flex-direction:column; gap:6px; padding:10px 14px; border-radius:9px; font-size:0.82rem; }
    .melding.rij { flex-direction:row; align-items:center; gap:10px; }
    .melding.groen { background:var(--mv-primary-light); border:1px solid var(--mv-primary-tint-border, #CFE6C8); color:var(--mv-primary-hover); }
    .melding.rood { background:var(--mv-danger-bg); border:1px solid var(--mv-danger-border, #F8C9D6); }
    .melding.rood strong { color:var(--mv-danger); }
    .regel { display:flex; justify-content:space-between; font-weight:600; }
    .tijd { font-variant-numeric:tabular-nums; }
    .tekst { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
    .tekst strong { font-size:0.82rem; }
    .tekst span { font-size:0.76rem; color:var(--mv-text-sub); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .tekstknop { border:0; background:none; padding:0; color:var(--mv-primary-hover); font-size:0.8rem; font-weight:600; }
    .knop-rood { flex:none; padding:6px 10px; border:1px solid var(--mv-danger-border, #F8C9D6); border-radius:8px;
                 background:var(--mv-surface); color:var(--mv-danger); font-size:0.8rem; font-weight:600; }
  `;
}

customElements.define('lab-certificaat', LabCertificaat);
